import { Children, isValidElement, type ReactNode } from 'react';

import { Card } from './Card';
import { cx } from './cx';
import { errorIdOf, useFieldError } from './fieldProblem';
import { Icon } from './Icon';

/**
 * A settings card: a header strip with a title and the sentence that says what the whole
 * card is about, then rows.
 *
 * Wider than a `CardHeader`, because a section is read before anything on it is changed and
 * a 13px title with no description is how a setting comes to be changed by somebody who did
 * not know what it did.
 */
export function Section({
  title,
  description,
  actions,
  className,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <Card className={className}>
      <header className="flex items-start justify-between gap-4 px-5 py-3 border-b border-rule">
        <div className="min-w-0">
          <h2 className="text-section font-semibold m-0">{title}</h2>
          {description ? (
            <p className="text-secondary text-ink-3 mt-0.5 mb-0 max-w-[72ch] leading-[1.4]">
              {description}
            </p>
          ) : null}
        </div>
        {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
      </header>
      {children}
    </Card>
  );
}

/**
 * One setting: its name and the sentence that stops it being mysterious on the left, the
 * control on the right in a fixed 260px column.
 *
 * Fixed, so that a column of switches and a column of numbers line up down the page rather
 * than each control sitting wherever its own label ended.
 *
 * When the last save was refused over this row's field, the row is tinted and says why
 * under its explanation. It knows its field from the `id` of the control it was given, the
 * way `Field` reads its child — every settings control carries the API's name for it as its
 * id already, so no row is told separately.
 */
export function SettingRow({
  label,
  help,
  last,
  className,
  children,
}: {
  label: ReactNode;
  help?: ReactNode;
  /** The last row in a section, which loses its rule so the card closes cleanly. */
  last?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  const problem = useFieldError(controlIds(children));

  return (
    <div
      className={cx(
        'grid grid-cols-[minmax(0,1fr)_260px] gap-6 items-center px-5 py-3',
        last ? '' : 'border-b border-rule',
        problem ? 'bg-danger-soft' : '',
        className,
      )}
    >
      <div className="min-w-0">
        <div className="text-body font-medium">{label}</div>
        {help ? (
          <div className="text-secondary text-ink-3 mt-0.5 max-w-[58ch] leading-[1.4]">{help}</div>
        ) : null}
        {problem ? (
          <div
            id={errorIdOf(problem.id)}
            className="flex items-center gap-1.5 text-secondary font-medium text-danger mt-1.5"
          >
            <Icon name="alert" small />
            <span className="min-w-0">{problem.message}</span>
          </div>
        ) : null}
      </div>
      <div className="flex justify-end items-center gap-2">{children}</div>
    </div>
  );
}

/** The ids of the controls a row was handed — the names of the fields it edits. */
function controlIds(children: ReactNode): string[] {
  return Children.toArray(children).flatMap((child) =>
    isValidElement<{ id?: unknown }>(child) && typeof child.props.id === 'string'
      ? [child.props.id]
      : [],
  );
}

/**
 * A rung of the dispatch ladder: a numbered disc, what the rung does, and the buttons that
 * move or remove it. The number is drawn rather than written into the label, because the
 * order is the setting.
 */
export function LadderRow({
  index,
  actions,
  last,
  className,
  children,
}: {
  index: number;
  actions?: ReactNode;
  last?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div
      className={cx(
        'grid grid-cols-[24px_1fr_auto] gap-3 items-center px-5 py-2.5',
        last ? '' : 'border-b border-rule',
        className,
      )}
    >
      <span className="w-[22px] h-[22px] rounded-full bg-surface-2 text-ink-2 text-[12px] font-semibold flex items-center justify-center">
        {index}
      </span>
      <div className="min-w-0">{children}</div>
      <div className="flex items-center gap-1">{actions}</div>
    </div>
  );
}
